import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Cookies from "universal-cookie";
import { focusHospital } from "state/slices/hospitalSlice";
import UserService from "services/UserService";
import PatientService from "services/PatientService";

const cookies = new Cookies();
const ALL_CENTRES_HOSPITAL_ID = 0;

function isValidHospitalId(id) {
  return id != null && id !== "" && Number(id) > 0;
}

function syncFocusedHospital(dispatch, hospitalId) {
  const id = Number(hospitalId);
  dispatch(focusHospital({ id }));
  cookies.set("hospital", id, { path: "/" });
}

/** Resolve patient centre via GET /hospitals/0/patients/{id}. */
async function fetchPatientHospitalId(patientId) {
  const response = await PatientService.getPatient([
    ALL_CENTRES_HOSPITAL_ID,
    patientId,
  ]);
  const patient = response?.data;
  if (!patient || String(patient.id) !== String(patientId)) {
    return null;
  }
  return patient.associatedHospitalId ?? null;
}

/**
 * Resolves the hospital id used in /hospitals/{id}/... API calls.
 * - Patient routes: always use the patient's centre and keep Redux/cookie in sync.
 * - Other routes: use outlet context, cookie, or first accessible hospital.
 */
export default function useResolvedHospitalId({
  contextHospitalId,
  patientId,
  syncRedux = false,
} = {}) {
  const dispatch = useDispatch();
  const [hospitalId, setHospitalId] = useState(null);
  const [resolvedPatientId, setResolvedPatientId] = useState(null);
  const [isResolvingNonPatient, setIsResolvingNonPatient] = useState(!patientId);
  const [resolveError, setResolveError] = useState(null);

  // Avoid one-frame stale hospital when switching patients (effect runs after paint).
  const isResolving = patientId
    ? String(resolvedPatientId ?? "") !== String(patientId)
    : isResolvingNonPatient;

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      setResolveError(null);
      if (!patientId) {
        setIsResolvingNonPatient(true);
      }

      try {
        let resolvedId = null;

        if (patientId) {
          try {
            resolvedId = await fetchPatientHospitalId(patientId);
          } catch (err) {
            // Lookup failed; try context hospital as last resort below.
          }

          if (!isValidHospitalId(resolvedId) && isValidHospitalId(contextHospitalId)) {
            try {
              const response = await PatientService.getPatient([
                Number(contextHospitalId),
                patientId,
              ]);
              if (response?.data && String(response.data.id) === String(patientId)) {
                resolvedId =
                  response.data.associatedHospitalId ?? Number(contextHospitalId);
              }
            } catch (err) {
              // keep resolvedId null
            }
          }
        } else if (isValidHospitalId(contextHospitalId)) {
          resolvedId = Number(contextHospitalId);
        } else {
          const hospitals = (await UserService.getHospitals())?.data ?? [];
          const cookieHospitalId = cookies.get("hospital");
          const matched = hospitals.find(
            (h) => String(h.id) === String(cookieHospitalId)
          );
          resolvedId = matched?.id ?? hospitals[0]?.id ?? null;
        }

        if (cancelled) return;

        if (!isValidHospitalId(resolvedId)) {
          setHospitalId(null);
          setResolvedPatientId(patientId ? String(patientId) : null);
          setResolveError(
            patientId
              ? "Patient not found or you do not have access to this patient's centre. Open the patient from the patients list or search, then try again."
              : "No centre is available for your account."
          );
          return;
        }

        const id = Number(resolvedId);
        setHospitalId(id);
        setResolvedPatientId(patientId ? String(patientId) : null);

        if (syncRedux && (patientId || id !== Number(contextHospitalId))) {
          syncFocusedHospital(dispatch, id);
        }
      } catch (err) {
        console.error("Failed to resolve hospital id", err);
        if (!cancelled) {
          setHospitalId(null);
          setResolvedPatientId(patientId ? String(patientId) : null);
          setResolveError("Could not determine the correct centre. Please try again.");
        }
      } finally {
        if (!cancelled && !patientId) {
          setIsResolvingNonPatient(false);
        }
      }
    }

    resolve();

    return () => {
      cancelled = true;
    };
  }, [contextHospitalId, patientId, syncRedux, dispatch]);

  return {
    hospitalId: isResolving ? null : hospitalId,
    isResolving,
    resolveError: isResolving ? null : resolveError,
  };
}
